import React, { useMemo } from "react";

// Inline-SVG price sparkline. Deliberately dependency-free (the site already
// ships chart.js, but for an 80x30 line that would be far heavier than a few
// <polyline> nodes — and CLAUDE.md makes speed a priority).
//
// Plots a runner's intraday Betfair back-price history as a moving line.
//
// Two colour modes:
//   - VALUE (tissue supplied): draws the tissue/fair price as a flat dashed
//     reference; line is GREEN when the current price is above tissue (bigger
//     odds than fair = value) and RED when below. Used on the members' tissue
//     pages.
//   - TREND (no tissue): no reference line — the tissue price is proprietary and
//     must not appear on public pages. Line is GREEN when the price has
//     shortened since the first sample (steamed in), RED when it has drifted,
//     grey when flat. Used on the public Today's Card.
//
// X-AXIS. Samples arrive roughly every 5 minutes, but "roughly" is doing real
// work: a container restart, a missed push or a Betfair hiccup leaves gaps.
// Spacing points by array index would draw a 40-minute gap the same width as a
// 5-minute one, so x is spaced by ELAPSED TIME (xMode="time", the default).
//
// xMode="volume" instead spaces x by money matched in the market (`m` on each
// sample), which stretches the busy run-up to the off and compresses the dead
// morning — most of a race's money trades in the last 15 minutes, so a
// time-spaced line spends most of its width on a market nobody is betting in.
// It degrades to time spacing whenever volume is absent or unusable (older
// samples predate the field, and races part-run at deploy time have a mix).
//
// Volume is spaced LOGARITHMICALLY, not linearly. Linear money is brutal: on a
// typical day it squeezes the entire morning into ~2% of the width, and the
// morning price is the one our members lock against — erasing it would make
// the graph worse for the thing it's read for. Log keeps the morning legible
// (~24%) while still giving the run-up most of the width.
//
// Props:
//   history  [{ t, price, m }] oldest-first samples (from /api/snapshots/{id})
//                              m = market total matched (£), optional
//   tissue   number|null     tissue/fair price for the reference line; omit for
//                            TREND mode (public pages)
//   xMode    "time"|"volume" horizontal spacing basis (default "time")
//   width,height             px (default 80x30)
const VALUE_GREEN = "#4A6B3A"; // brand Turf
const SHORT_RED = "#C0392B";
const TISSUE_GREY = "#A08060"; // brand Tan

// Returns a 0..1 position per sample, or null if this basis can't be used.
// `values` must be a non-decreasing series; a flat or single-valued series is
// rejected so the caller can fall back.
function normalise(values) {
  if (!values || values.some((v) => v == null || !Number.isFinite(v))) return null;
  const lo = values[0];
  const hi = values[values.length - 1];
  const span = hi - lo;
  if (!(span > 0)) return null; // all samples identical -> no basis to spread on
  return values.map((v) => (v - lo) / span);
}

// Market total-matched is cumulative in principle, but Betfair serves values
// that jitter DOWN by a few percent between polls (observed 50047 -> 52492 ->
// 48022 on one market). Left alone that makes the line double back on itself,
// so clamp to a running max.
function runningMax(values) {
  let peak = -Infinity;
  return values.map((v) => {
    if (v != null && Number.isFinite(v) && v > peak) peak = v;
    return peak === -Infinity ? null : peak;
  });
}

// 0..1 positions from a series of market total-matched values, or null if the
// series can't carry the axis (all missing, or never grows). Clamped to a
// running max, then log-scaled — see the note at the top of the file.
function volumeFractions(matched) {
  return normalise(
    runningMax(matched).map((v) =>
      // Non-positive money has no log; map it to null so normalise rejects the
      // whole series and we fall back to time rather than mixing raw and
      // log-scaled values on one axis.
      v == null || !(v > 0) ? null : Math.log(v)
    )
  );
}

export default function PriceSparkline({
  history,
  tissue,
  xMode = "time",
  width = 80,
  height = 30,
}) {
  const view = useMemo(() => {
    const pts = (history || []).filter((p) => p && p.price > 0);
    if (pts.length === 0) return null;

    const prices = pts.map((p) => p.price);
    const refs = tissue != null && tissue > 0 ? [tissue] : [];
    const lo = Math.min(...prices, ...refs);
    const hi = Math.max(...prices, ...refs);

    const padX = 2;
    const padY = 3;
    const span = hi - lo || 1; // avoid divide-by-zero on a flat line
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    // Higher price (bigger odds) plots higher on the chart -> invert y.
    const y = (price) => padY + innerH * (1 - (price - lo) / span);

    // Horizontal basis, best-available first: money matched (if asked for and
    // usable) -> elapsed time -> even index. The last is a true last resort
    // (identical timestamps, or a single sample).
    const byVolume =
      xMode === "volume" ? volumeFractions(pts.map((p) => p.m)) : null;
    const byTime = byVolume ? null : normalise(pts.map((p) => Date.parse(p.t)));
    const fracs =
      byVolume ||
      byTime ||
      pts.map((_, i) => (pts.length === 1 ? 0.5 : i / (pts.length - 1)));

    const x = (i) => (pts.length === 1 ? width / 2 : padX + innerW * fracs[i]);

    const linePts = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.price).toFixed(1)}`);
    const last = prices[prices.length - 1];
    const first = prices[0];
    let color;
    if (tissue != null && tissue > 0) {
      // VALUE mode: above tissue = value.
      color = last >= tissue ? VALUE_GREEN : SHORT_RED;
    } else {
      // TREND mode: shortened = green, drifted = red, flat = grey.
      color = last < first ? VALUE_GREEN : last > first ? SHORT_RED : TISSUE_GREY;
    }
    const tissueY = refs.length ? y(tissue) : null;
    const lastX = x(pts.length - 1);
    const lastY = y(last);

    // Money matched in the MARKET (not on this runner — Betfair doesn't give
    // us that). Still worth surfacing on hover: the same price move means
    // rather more in a £200k market than in a £6k one.
    const matched = pts.reduce(
      (acc, p) => (p.m != null && Number.isFinite(p.m) && p.m > acc ? p.m : acc),
      0
    );
    const label =
      (byVolume ? "Price by money matched" : "Price through the day") +
      (matched ? ` — £${Math.round(matched).toLocaleString()} matched in this market` : "");

    return {
      linePts,
      color,
      tissueY,
      lastX,
      lastY,
      label,
      single: pts.length === 1,
    };
  }, [history, tissue, xMode, width, height]);

  if (!view) return null;

  return (
    <svg
      className="price-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={view.label}
    >
      <title>{view.label}</title>
      {view.tissueY != null && (
        <line
          x1={0}
          x2={width}
          y1={view.tissueY}
          y2={view.tissueY}
          stroke={TISSUE_GREY}
          strokeWidth={1}
          strokeDasharray="2 2"
        />
      )}
      {!view.single && (
        <polyline
          points={view.linePts.join(" ")}
          fill="none"
          stroke={view.color}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      <circle cx={view.lastX} cy={view.lastY} r={1.8} fill={view.color} />
    </svg>
  );
}
